import React, { useEffect, useRef, useState } from "react";

/**
 * A row of controls that scrolls sideways inside itself when it outruns its
 * frame (#11742 round 7, Claude Design ruling on the agent tab strip):
 * quiet bar, an edge fade on whichever side still has content beyond it —
 * a mask over the strip's own ground, so it survives on bone as well as
 * white — and the selected child scrolled into view on mount, so landing on
 * a tab past the fold never shows a different tab selected. The fade is a
 * static affordance, not motion, so reduced-motion keeps it. Four tabs
 * scroll; past that a strip should fold into a menu by the toolbar rule.
 *
 * The strip is `overflow-x: auto` at every width (one scroller behavior,
 * not a phone variant); per the overflow spec that also clips vertical
 * overflow, so only flat control rows belong inside it.
 */
const FADE_PX = 24;

const maskFor = (left: boolean, right: boolean): string | undefined => {
  if (left && right) {
    return `linear-gradient(to right, transparent, black ${FADE_PX}px, black calc(100% - ${FADE_PX}px), transparent)`;
  }
  if (left) return `linear-gradient(to right, transparent, black ${FADE_PX}px)`;
  if (right) {
    return `linear-gradient(to right, black calc(100% - ${FADE_PX}px), transparent)`;
  }
  return undefined;
};

const SELECTED_CHILD =
  "[aria-current]:not([aria-current='false']), [aria-selected='true'], [aria-pressed='true'], [data-active='true']";

/** The strip a control sits in, for a control that has to keep itself in
 *  view while something anchored to it is open. */
export const scrollStripOf = (el: HTMLElement): HTMLElement | null =>
  el.closest<HTMLElement>("[data-scroll-strip]");

// Scroll the strip itself — never an ancestor — until the child is inside
// the strip's window. `scrollIntoView` would also pull the page to a strip
// below the fold on mount. Exported for a strip whose selection changes
// after mount (the entity tabs' view chips, #12023).
export const revealWithinStrip = (
  strip: HTMLElement,
  child: HTMLElement
): void => {
  const stripRect = strip.getBoundingClientRect();
  const childRect = child.getBoundingClientRect();
  const childLeft = childRect.left - stripRect.left + strip.scrollLeft;
  const childRight = childLeft + childRect.width;
  if (childLeft < strip.scrollLeft) {
    strip.scrollLeft = childLeft;
  } else if (childRight > strip.scrollLeft + strip.clientWidth) {
    strip.scrollLeft = childRight - strip.clientWidth;
  }
};

export const QuietScrollStrip: React.FC<{
  className?: string;
  /** More `data-*` attributes for the scroller, by which a host's surface is found. */
  attributes?: Readonly<Record<`data-${string}`, string>>;
  children: React.ReactNode;
}> = ({ className = "", attributes, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState<{ left: boolean; right: boolean }>({
    left: false,
    right: false,
  });

  const sync = () => {
    const el = ref.current;
    if (!el) return;
    setEdges((prev) => {
      const next = {
        left: el.scrollLeft > 1,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
      };
      return prev.left === next.left && prev.right === next.right ? prev : next;
    });
  };

  // The strip's own size is what the ResizeObserver below watches; its
  // CONTENT can outgrow it without that changing (a composer's attachment
  // row gaining chips, #11207), so re-read the edges after every render too.
  useEffect(() => {
    sync();
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // The selected child leads: bring it into view before the first sync
    // so the fades describe the settled position.
    const selected = el.querySelector<HTMLElement>(SELECTED_CHILD);
    if (selected) revealWithinStrip(el, selected);
    sync();
    el.addEventListener("scroll", sync, { passive: true });
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    // The content too: a strip whose own width is fixed by its frame still
    // overflows when a control inside it grows (a chip taking a value), and
    // the fade is the only cue that anything lies past the fold (#6559
    // round 9 review). The row's box does not change size when its children
    // outrun it, so a resize observer on it would miss exactly that; watch
    // the subtree's mutations instead — a label changing, a chip appearing.
    // Only the attributes that can change a width: an aria flip cannot, and
    // this component's own fade attributes must not feed back into it.
    const mutations = new MutationObserver(sync);
    mutations.observe(el, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden"],
    });
    return () => {
      el.removeEventListener("scroll", sync);
      observer.disconnect();
      mutations.disconnect();
    };
  }, []);

  const mask = maskFor(edges.left, edges.right);
  return (
    <div
      ref={ref}
      {...attributes}
      data-scroll-strip=""
      data-fade-left={edges.left ? "" : undefined}
      data-fade-right={edges.right ? "" : undefined}
      className={`overflow-x-auto fp-scroll-quiet ${className}`}
      style={{ maskImage: mask, WebkitMaskImage: mask }}
    >
      {children}
    </div>
  );
};
